// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable sonarjs/cognitive-complexity */
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import Auth from 'modules/Auth';
import cx from 'classnames';

import infoSvg from 'assets/icons/info.svg';
import tagSvg from 'assets/icons/tag.svg';
import listSvg from 'assets/icons/list.svg';
import { getIsReseller } from 'store/publisher/selectors';
import { PermissionsEnum } from 'consts/permissions';
import Card from 'components/common/Card';
import Button, { Color, Size } from 'lib/Button';
import Input from 'lib/Input';
import AsyncSelect from 'lib/AsyncSelect';
import { TableHeight } from 'lib/Table/Table.types';
import { Store } from 'components/common/types/Store.types';
import { CreativeStatus } from 'components/common/types/Creative.types';
import { FeatureFlags } from 'components/common/types/Features.types';
import { FilterProductCategory } from 'components/common/FilterArea/Filters';
import { useHasFeatureAccess } from 'customHooks/useHasFeatureAccess';
import usePendoTracking from 'customHooks/usePendoTracking';
import { isCreativeCommercialsRequired } from '../../utils';
import AdditionalProductCategories from './AdditionalProductCategories';
import { TaggingRulesTable } from './TaggingRulesTable/TaggingRulesTable';
import { useTaggingRules } from './useTaggingRules';
import { TaggingRulesProps } from './TaggingRules.types';
import ProductFormats from './ProductFormats';

const TaggingRules = ({
  commercialId,
  creativeId,
  creativeStatus,
  frameSummary,
  isDisabled,
  isFetchingCreativeDetails,
  marketId,
  onTaggingRulesChange,
}: TaggingRulesProps): React.ReactElement => {
  const {
    creative,
    handleSelectProductCategory,
    handleExportSummary,
    handleLoadAgencyLookupData,
    handleLoadFrameTagsLookupData,
    handleLoadSpecialistLookupData,
    handleSelectAdvertiser,
    handleSelectAgency,
    handleSelectBrand,
    handleSelectFrameTag,
    handleSelectSpecialist,
    onLoadBrandsData,
    onLoadAdvertisersData,
  } = useTaggingRules(creativeId, commercialId, onTaggingRulesChange);

  const {
    advertiser,
    agency,
    brand,
    externalAdvertiserName,
    clickThroughUrl,
    externalAgencyId,
    frameTags,
    productCategory,
    specialist,
    uploadedBy,
  } = creative;

  const isFramesAvailable = frameSummary?.inclusionListCount || frameSummary?.exclusionListCount;
  const isApproved = creativeStatus === CreativeStatus.APPROVED;
  const isReseller = useSelector(getIsReseller);
  const isApproveWithoutCommercialEnabled = useHasFeatureAccess(FeatureFlags.APPROVE_WITHOUT_COMMERCIAL);
  const isAdvertiserBrandRequired = isCreativeCommercialsRequired(
    isApproveWithoutCommercialEnabled,
    externalAdvertiserName,
  );
  const { getPendoDataId } = usePendoTracking();

  const meta = useSelector((state: Store) => state.dealManagement.meta);

  const displayCreativeDetails = uploadedBy === 'Exchange Submission' || !isReseller;

  const showFrameAvailability =
    Auth.hasPermission(PermissionsEnum.CONTENT_CREATIVE_CATEGORIES_VIEW) &&
    Auth.hasPermission(PermissionsEnum.CONTENT_WHITELIST_BACKLIST);

  const showExportButton =
    showFrameAvailability && Auth.hasPermission(PermissionsEnum.CONTENT_WHITELIST_BACKLIST_EXPORT);

  const showFramesSummary = !isFetchingCreativeDetails && isFramesAvailable;

  const showAdditionalProductCategories = Auth.hasPermission(PermissionsEnum.CONTENT_CREATIVE_CATEGORIES_VIEW);

  const productCategoriesOptions = useMemo(
    () => meta.productCategoryGroups.map((productCategoryGroup) => productCategoryGroup.productCategories!).flat(),
    [meta.productCategoryGroups],
  );

  return (
    <>
      {showFrameAvailability && (
        <Card>
          <div className="text-neutral-600">
            <div className="mb-1 body-sm">
              Frame availability is based on the targeting rules you have assigned to the creative
            </div>
            {showExportButton && (
              <Button
                onClick={handleExportSummary}
                color={Color.PRIMARY}
                isDisabled={isDisabled}
                size={Size.LARGE}
                label="Export frames list"
                dataPendoId={getPendoDataId('Export Frames List Button')}
              />
            )}
            {showFramesSummary ? (
              <div className="mt-1 body-base">
                <p>
                  <b>Included: </b>
                  {frameSummary.inclusionListCount} frames
                </p>
                <p>
                  <b>Excluded: </b>
                  {frameSummary.exclusionListCount} frames
                </p>
              </div>
            ) : (
              <div className="mt-1 body-base min-h-[40px]" data-test-id="no-frames-available-info">
                Please check your creative <span>Motion</span> type, link a <span>Deal ID</span>, assign the advertiser{' '}
                <span>Product category</span>, and add a <span>Frame tag</span> to validate the eligible frames.
              </div>
            )}
          </div>
        </Card>
      )}
      {displayCreativeDetails && (
        <>
          <Card
            header="Creative details"
            icon={infoSvg}
            info={!isReseller ? 'Provide the Advertiser, Brand & Product category details of the creative' : undefined}
          >
            <div className="grid grid-cols-2 gap-x-24 gap-y-6 mb-3">
              {!isReseller && (
                <>
                  <div className="w-full" data-test-id="advertiser">
                    <span className="inline-block mb-2.5 sub-header-base text-neutral-600">
                      Advertiser {isAdvertiserBrandRequired && <i className="body-base text-pinkRed-500">*</i>}
                    </span>
                    <AsyncSelect
                      dataTestId="advertiser-input"
                      name="advertiser"
                      loadData={onLoadAdvertisersData}
                      selectedValue={advertiser?.code ? advertiser : null}
                      placeholder="Select advertiser"
                      onSelect={handleSelectAdvertiser}
                      defaultOptions={brand?.advertisers ? brand.advertisers : undefined}
                      isDisabled={isApproved || isDisabled}
                    />
                  </div>
                  <div className="w-full" data-test-id="brand">
                    <span className="inline-block mb-2.5 sub-header-base text-neutral-600">
                      Brand {isAdvertiserBrandRequired && <i className="body-base text-pinkRed-500">*</i>}
                    </span>
                    <AsyncSelect
                      dataTestId="brand-input"
                      name="brand"
                      loadData={onLoadBrandsData}
                      selectedValue={brand?.code ? brand : null}
                      placeholder="Select brand"
                      onSelect={handleSelectBrand}
                      defaultOptions={advertiser?.code ? advertiser.brands : undefined}
                      isDisabled={isApproved || isDisabled}
                    />
                  </div>
                  <div className="w-full" data-test-id="product-category">
                    <span className="inline-block mb-2.5 sub-header-base text-neutral-600">Product category</span>
                    <FilterProductCategory
                      dataTestId="product-category-select"
                      options={productCategoriesOptions}
                      selectedValue={{ code: productCategory?.code || '', name: productCategory?.name || '' }}
                      onSelect={handleSelectProductCategory}
                      isDisabled={isApproved || isDisabled}
                    />
                  </div>
                  <div className="w-full" data-test-id="agency">
                    <span className="inline-block mb-2.5 sub-header-base text-neutral-600">Agency</span>
                    <AsyncSelect
                      dataTestId="agency-input"
                      name="agency"
                      loadData={handleLoadAgencyLookupData}
                      selectedValue={agency?.code ? agency : null}
                      placeholder="Select agency"
                      onSelect={handleSelectAgency}
                      defaultOptions={[]}
                      isDisabled={isDisabled}
                      dataPendoId={getPendoDataId('Agency')}
                    />
                  </div>
                  <div className="w-full" data-test-id="specialist">
                    <span className="inline-block mb-2.5 sub-header-base text-neutral-600">Specialist</span>
                    <AsyncSelect
                      dataTestId="specialist-input"
                      name="specialist"
                      loadData={handleLoadSpecialistLookupData}
                      selectedValue={specialist?.code ? specialist : null}
                      placeholder="Select specialist"
                      onSelect={handleSelectSpecialist}
                      defaultOptions={[]}
                      isDisabled={isDisabled}
                      dataPendoId={getPendoDataId('Specialist')}
                    />
                  </div>
                </>
              )}
              {uploadedBy === 'Exchange Submission' && (
                <>
                  <div className="w-full" data-test-id="external-advertiser-name">
                    <span className="inline-block mb-2.5 sub-header-base text-neutral-600">DSP advertiser name</span>
                    <div className="opacity-70 cursor-not-allowed pointer-events-none">
                      <Input
                        dataTestId="readonly-external-advertiser-name-input"
                        name="externalAdvertiserName"
                        value={externalAdvertiserName}
                        isDisabled
                      />
                    </div>
                  </div>
                  <div className="w-full col-start-2" data-test-id="external-agency-id">
                    <span className="inline-block mb-2.5 sub-header-base text-neutral-600">DSP seat ID</span>
                    <div className="opacity-70 cursor-not-allowed pointer-events-none">
                      <Input
                        dataTestId="readonly-external-agency-id-input"
                        name="externalAgencyId"
                        value={externalAgencyId}
                        isDisabled
                      />
                    </div>
                  </div>
                  <div
                    className={cx('w-full ', isReseller ? 'col-span-1' : 'col-start-2')}
                    data-test-id="click-through-url"
                  >
                    <span className="inline-block mb-2.5 sub-header-base text-neutral-600">DSP advertiser domain</span>
                    <div className="opacity-70 cursor-not-allowed pointer-events-none">
                      <Input
                        dataTestId="readonly-click-through-url-input"
                        name="clickThroughUrl"
                        value={clickThroughUrl}
                        isDisabled
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          </Card>
          {!isReseller && (
            <>
              <Card
                header="Product formats"
                info="Attribute your creative with a Product format to determine frame availability"
                icon={listSvg}
              >
                <ProductFormats isDisabled={isApproved || isDisabled} onTaggingRulesChange={onTaggingRulesChange} />
              </Card>
              {showAdditionalProductCategories && (
                <Card
                  header="Additional product categories"
                  info="Additional categories can optionally be assigned to the creative"
                  icon={listSvg}
                >
                  <AdditionalProductCategories
                    marketId={marketId}
                    creativeStatus={creativeStatus}
                    onTaggingRulesChange={onTaggingRulesChange}
                    isDisabled={isDisabled}
                  />
                </Card>
              )}
              <Card header="Frame tagging rules" icon={tagSvg} info="Assign tags for additional frame targeting">
                <div className="row">
                  <div className="col-12">
                    <div className="row">
                      <div className="col-12 col-md-4">
                        <div className="form-group mb-3" data-test-id="tags">
                          <span className="sub-header-base form-label">Frame tags</span>
                          <AsyncSelect
                            dataTestId="tags-input"
                            name="tags"
                            loadData={handleLoadFrameTagsLookupData}
                            selectedValue={null}
                            placeholder="Select a frame tag"
                            onSelect={handleSelectFrameTag}
                            textKey="compositeName"
                            isDisabled={isDisabled}
                            dataPendoId={getPendoDataId('Select Frame Tag')}
                          />
                        </div>
                      </div>
                    </div>
                    {frameTags.length > 0 && (
                      <div className="row">
                        <div className="col-12">
                          <TaggingRulesTable
                            frameTags={frameTags}
                            tableHeight={TableHeight.AUTO}
                            onTaggingRulesChange={onTaggingRulesChange}
                            marketId={marketId}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            </>
          )}
        </>
      )}
    </>
  );
};

export default TaggingRules;
